import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy } from "react";
import { ThemeProvider } from "@/context/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// Loading Component
const PageLoader = () => (
  <div className="min-h-screen bg-[#faf7f2] flex items-center justify-center">
    <div className="animate-pulse">
      <div className="w-12 h-12 border-4 border-[#1c1510] border-t-transparent rounded-full animate-spin"></div>
    </div>
  </div>
);

// Shared Landing Page - Load immediately for fast first paint
import SharedLandingPage from "@/pages/SharedLandingPage";

// Leadership Pages - Lazy loaded
const HomePage = lazy(() => import("@/pages/HomePage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const IndividualSessionPage = lazy(() => import("@/pages/IndividualSessionPage"));
const TenSessionPage = lazy(() => import("@/pages/TenSessionPage"));
const YearLongPage = lazy(() => import("@/pages/YearLongPage"));
const RetreatPage = lazy(() => import("@/pages/RetreatPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));

// Workshops
const WorkshopsPage = lazy(() => import("@/pages/WorkshopsPage"));

// Host Page - Hidden URL (not in navigation)
const HostPage = lazy(() => import("@/pages/HostPage"));
const HostDashboardPage = lazy(() => import("@/pages/HostDashboardPage"));

// Quiz & Results Flow
const QuizPage = lazy(() => import("@/pages/QuizPage"));
const AssessmentAboutPage = lazy(() => import("@/pages/AssessmentAboutPage"));
const ResultsSharePage = lazy(() => import("@/pages/ResultsSharePage"));
const ResultsSalesPage = lazy(() => import("@/pages/ResultsSalesPage"));
const PostPurchaseUpsell = lazy(() => import("@/pages/PostPurchaseUpsell"));
const PurchaseSuccessPage = lazy(() => import("@/pages/PurchaseSuccessPage"));
const ArchetypeSuccessPage = lazy(() => import("@/pages/ArchetypeSuccessPage"));

// Legal Pages - Lazy loaded
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("@/pages/legal/TermsPage"));
const CookiesPolicyPage = lazy(() => import("@/pages/legal/CookiesPolicyPage"));
const InformedConsentPage = lazy(() => import("@/pages/legal/InformedConsentPage"));
const DisclaimerPage = lazy(() => import("@/pages/legal/DisclaimerPage"));
const AccessibilityPage = lazy(() => import("@/pages/legal/AccessibilityPage"));
const RefundPolicyPage = lazy(() => import("@/pages/legal/RefundPolicyPage"));

// Error Pages - Lazy loaded
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

// Evidence Base Page
const EvidenceBasePage = lazy(() => import("@/pages/EvidenceBasePage"));

// Sage $47 funnel — standalone tripwire pages
const SageLandingPage = lazy(() => import("@/pages/SageLandingPage"));
const SageSuccessPage = lazy(() => import("@/pages/SageSuccessPage"));
const SageNextStepPage = lazy(() => import("@/pages/SageNextStepPage"));
const SageBookedPage = lazy(() => import("@/pages/SageBookedPage"));

// Pilot Programme Page - HIDDEN (SaaS offering moved to separate site)
// const PilotProgramPage = lazy(() => import("@/pages/PilotProgramPage"));

// Email Confirmation Page
const ConfirmEmailPage = lazy(() => import("@/pages/ConfirmEmailPage"));

// Thank You / Checkout Success Page - Lazy loaded
const ThankYouPage = lazy(() => import("@/pages/ThankYouPage"));
const PrivateBetaThankYouPage = lazy(() => import("@/pages/PrivateBetaThankYouPage"));
const PrivateBetaLandingPage = lazy(() => import("@/pages/PrivateBetaLandingPage"));
const BrooklynEllerLandingPage = lazy(() => import("@/pages/SmallBusinessLandingPage"));
const CBFLeadershipRecalibrationPage = lazy(() => import("@/pages/CBFLeadershipRecalibrationPage"));
const CBFEnrolmentPage = lazy(() => import("@/pages/CBFEnrolmentPage"));
const CBFWelcomePage = lazy(() => import("@/pages/CBFWelcomePage"));
const CBFAdvantagePage = lazy(() => import("@/pages/CBFAdvantagePage"));
const AgencyLoopTermsPage = lazy(() => import("@/pages/legal/AgencyLoopTermsPage"));
const CBFAdvantageSuccessPage = lazy(() => import("@/pages/CBFAdvantageSuccessPage"));
const LeadershipRecalibrationApplyPage = lazy(() => import("@/pages/LeadershipRecalibrationApplyPage"));
const AffiliateDashboardPage = lazy(() => import("@/pages/AffiliateDashboardPage"));
const CBFVerifyAgreementPage = lazy(() => import("@/pages/CBFVerifyAgreementPage"));
const CBFPortalPage = lazy(() => import("@/pages/CBFPortalPage"));
const MyReportsPage = lazy(() => import("@/pages/MyReportsPage"));
const TheFrameworkPage = lazy(() => import("@/pages/TheFrameworkPage"));

// Admin Dashboard - Lazy loaded
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));

// Email Management Pages - Lazy loaded
const UnsubscribePage = lazy(() => import("@/pages/UnsubscribePage"));
const EmailPreferencesPage = lazy(() => import("@/pages/EmailPreferencesPage"));

// Knowledge Framework Pages - Lazy loaded
const KnowledgeIndexPage = lazy(() => import("@/pages/knowledge/KnowledgeIndexPage"));
const DecisionMakingUnderPressurePage = lazy(() => import("@/pages/knowledge/DecisionMakingUnderPressurePage"));
const LossOfDirectiveControlPage = lazy(() => import("@/pages/knowledge/LossOfDirectiveControlPage"));
const CommunicationUnderLoadPage = lazy(() => import("@/pages/knowledge/CommunicationUnderLoadPage"));

// Blog Pages - Lazy loaded
const BlogIndexPage = lazy(() => import("@/pages/blog/BlogIndexPage"));
const BlogPostPage = lazy(() => import("@/pages/blog/BlogPostPage"));
const BlogCategoryPage = lazy(() => import("@/pages/blog/BlogCategoryPage"));
const BlogTagPage = lazy(() => import("@/pages/blog/BlogTagPage"));
const BlogAuthorPage = lazy(() => import("@/pages/blog/BlogAuthorPage"));
const PillarPage = lazy(() => import("@/pages/blog/PillarPage"));
const CognitiveOverloadPage = lazy(() => import("@/pages/knowledge/CognitiveOverloadPage"));
const ConativeActionPage = lazy(() => import("@/pages/knowledge/ConativeActionPage"));
const KolbeInstinctiveActionPage = lazy(() => import("@/pages/knowledge/KolbeInstinctiveActionPage"));
const InstinctiveVsDeliberateActionPage = lazy(() => import("@/pages/knowledge/InstinctiveVsDeliberateActionPage"));
const DecisionReadinessPage = lazy(() => import("@/pages/knowledge/DecisionReadinessPage"));
const ProtectiveStrategiesPage = lazy(() => import("@/pages/knowledge/ProtectiveStrategiesPage"));
const BeliefsAndBehaviourPage = lazy(() => import("@/pages/knowledge/BeliefsAndBehaviourPage"));
const PsychKBehaviourChangePage = lazy(() => import("@/pages/knowledge/PsychKBehaviourChangePage"));
const IntegratingInstinctAndBeliefPage = lazy(() => import("@/pages/knowledge/IntegratingInstinctAndBeliefPage"));
const FocusedSessionPage = lazy(() => import("@/pages/FocusedSessionPage"));
const ScalingLeadershipPage = lazy(() => import("@/pages/ScalingLeadershipPage"));

// Layout & Navigation - Load immediately for fast first paint
import Layout from "@/components/Layout";
import ScrollToTopOnNavigate from "@/components/ScrollToTopOnNavigate";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import LeadMagnetPopup from "@/components/LeadMagnetPopup";

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <ThemeProvider>
          <div className="App">
            <BrowserRouter>
              <ScrollToTopOnNavigate />
              <ExitIntentPopup />
              <LeadMagnetPopup />
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Standalone landing pages (no shared header/footer/sticky CTA) */}
                <Route path="/smallbusiness" element={<BrooklynEllerLandingPage />} />
                <Route path="/cbf-leadership-recalibration" element={<CBFLeadershipRecalibrationPage />} />
                <Route path="/cbf-enrolment" element={<CBFEnrolmentPage />} />
                <Route path="/cbf-welcome" element={<CBFWelcomePage />} />
                <Route path="/cbf-advantage" element={<CBFAdvantagePage />} />
                <Route path="/cbf-advantage/success" element={<CBFAdvantageSuccessPage />} />
                <Route path="/agency-loop/terms-of-sale" element={<AgencyLoopTermsPage />} />
                <Route path="/cbf-advantage/terms" element={<Navigate to="/agency-loop/terms-of-sale" replace />} />
                <Route path="/leadership-recalibration/apply" element={<LeadershipRecalibrationApplyPage />} />
                <Route path="/affiliate" element={<AffiliateDashboardPage />} />
                <Route path="/verify-agreement" element={<CBFVerifyAgreementPage />} />
                <Route path="/cbf-portal" element={<CBFPortalPage />} />
                <Route path="/my-reports" element={<MyReportsPage />} />
                <Route path="/client-portal" element={<ClientPortalPage />} />
                <Route path="/cbf" element={<Navigate to="/cbf-leadership-recalibration" replace />} />
                <Route path="/brooklyneller" element={<Navigate to="/smallbusiness" replace />} />
                <Route path="/brooklyn-eller" element={<Navigate to="/smallbusiness" replace />} />
                <Route path="/small-business" element={<Navigate to="/smallbusiness" replace />} />

                {/* Sage $47 Pattern Diagnostic funnel — standalone (no nav distraction) */}
                <Route path="/sage" element={<SageLandingPage />} />
                <Route path="/sage/success" element={<SageSuccessPage />} />
                <Route path="/sage/next-step" element={<SageNextStepPage />} />
                <Route path="/sage/booked" element={<SageBookedPage />} />

                <Route element={<Layout />}>
                  {/* Shared Landing Page - Root */}
                  <Route path="/" element={<SharedLandingPage />} />

                  {/* The Framework (long-form SEO/AI page) */}
                  <Route path="/the-framework" element={<TheFrameworkPage />} />
                  <Route path="/framework" element={<Navigate to="/the-framework" replace />} />
                  <Route path="/methodology" element={<Navigate to="/the-framework" replace />} />
                  
                  {/* Leadership Funnel */}
                <Route path="/leadership" element={<HomePage />} />
                <Route path="/leadership/about" element={<AboutPage />} />
                <Route path="/leadership/services" element={<ServicesPage />} />
                <Route path="/leadership/services/individual-session" element={<IndividualSessionPage />} />
                <Route path="/leadership/services/10-session-program" element={<TenSessionPage />} />
                <Route path="/leadership/services/12-month-immersion" element={<YearLongPage />} />
                <Route path="/leadership/services/retreat" element={<RetreatPage />} />
                <Route path="/leadership/services/focused-session" element={<FocusedSessionPage />} />
                <Route path="/leadership/scaling" element={<ScalingLeadershipPage />} />
                <Route path="/leadership/faq" element={<FAQPage />} />
                
                {/* Workshops */}
                <Route path="/workshops" element={<WorkshopsPage />} />
                <Route path="/workshop/success" element={<PurchaseSuccessPage />} />
                
                {/* Host Page - Hidden URL (not in navigation) */}
                <Route path="/host" element={<HostPage />} />
                <Route path="/host/dashboard" element={<HostDashboardPage />} />
                
                {/* Quiz */}
                <Route path="/quiz" element={<QuizPage />} />
                <Route path="/assessment" element={<AssessmentAboutPage />} />
                
                {/* Quiz Results - Shareable Free Results */}
                <Route path="/results/:archetypeSlug" element={<ResultsSharePage />} />
                <Route path="/results/:archetypeSlug/success" element={<ArchetypeSuccessPage />} />
                
                {/* Sales Pages */}
                <Route path="/sales/:archetypeSlug" element={<ResultsSalesPage />} />
                
                {/* Purchase Flow */}
                <Route path="/upsell" element={<PostPurchaseUpsell />} />
                <Route path="/purchase-success" element={<PurchaseSuccessPage />} />
                
                {/* Shared Legal Pages */}
                <Route path="/legal/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/legal/terms-and-conditions" element={<TermsPage />} />
                <Route path="/legal/terms" element={<TermsPage />} />
                <Route path="/legal/cookies-policy" element={<CookiesPolicyPage />} />
                <Route path="/legal/informed-consent" element={<InformedConsentPage />} />
                <Route path="/legal/disclaimer" element={<DisclaimerPage />} />
                <Route path="/legal/accessibility" element={<AccessibilityPage />} />
                <Route path="/legal/refund-policy" element={<RefundPolicyPage />} />
                
                {/* Thank You / Post-Payment Page */}
                <Route path="/thank-you" element={<ThankYouPage />} />
                <Route path="/thank-you/private-beta" element={<PrivateBetaThankYouPage />} />

                {/* Private Beta Landing Page — standalone (not in blog system) */}
                <Route path="/private-beta" element={<PrivateBetaLandingPage />} />
                <Route path="/private-beta-may-2026" element={<PrivateBetaLandingPage />} />
                
                {/* Email Confirmation */}
                <Route path="/confirm-email" element={<ConfirmEmailPage />} />
                
                {/* Admin Dashboard */}
                <Route path="/admin" element={<AdminDashboard />} />
                
                {/* Knowledge Framework Pages */}
                <Route path="/knowledge" element={<KnowledgeIndexPage />} />
                <Route path="/knowledge/decision-making-under-pressure" element={<DecisionMakingUnderPressurePage />} />
                <Route path="/knowledge/loss-of-directive-control" element={<LossOfDirectiveControlPage />} />
                <Route path="/knowledge/communication-under-load" element={<CommunicationUnderLoadPage />} />
                <Route path="/knowledge/cognitive-overload" element={<CognitiveOverloadPage />} />
                <Route path="/knowledge/conative-action" element={<ConativeActionPage />} />
                <Route path="/knowledge/kolbe-instinctive-action" element={<KolbeInstinctiveActionPage />} />
                <Route path="/knowledge/instinctive-vs-deliberate-action" element={<InstinctiveVsDeliberateActionPage />} />
                <Route path="/knowledge/decision-readiness" element={<DecisionReadinessPage />} />
                <Route path="/knowledge/protective-strategies" element={<ProtectiveStrategiesPage />} />
                <Route path="/knowledge/beliefs-and-behaviour" element={<BeliefsAndBehaviourPage />} />
                <Route path="/knowledge/psych-k-behaviour-change" element={<PsychKBehaviourChangePage />} />
                <Route path="/knowledge/integrating-instinct-and-belief" element={<IntegratingInstinctAndBeliefPage />} />
                
                {/* Blog Routes */}
                <Route path="/blog" element={<BlogIndexPage />} />
                {/* /blog/decision-readiness-under-pressure now lives in the blog DB
                    and is rendered via the dynamic BlogPostPage route below. */}
                <Route path="/blog/category/:slug" element={<BlogCategoryPage />} />
                <Route path="/blog/tag/:slug" element={<BlogTagPage />} />
                <Route path="/blog/author/:slug" element={<BlogAuthorPage />} />
                {/* Legacy redirect: old unlisted blog URL -> standalone landing page */}
                <Route path="/blog/private-beta-may-2026" element={<Navigate to="/private-beta" replace />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                
                {/* Evidence & Research */}
                <Route path="/evidence" element={<EvidenceBasePage />} />

                {/* Legacy redirects — prevent soft-404s flagged by SEO audits (Apr 2026). */}
                {/* /apply and /pilot → Pilot Programme was removed; send to Services */}
                <Route path="/apply" element={<Navigate to="/leadership/services" replace />} />
                <Route path="/pilot" element={<Navigate to="/leadership/services" replace />} />
                {/* Knowledge URLs that were in old sitemaps but never shipped */}
                <Route path="/knowledge/stress-architecture" element={<Navigate to="/knowledge" replace />} />
                <Route path="/knowledge/capacity-building" element={<Navigate to="/knowledge/decision-readiness" replace />} />
                {/* Common legacy bare paths from external inbound links */}
                <Route path="/about" element={<Navigate to="/leadership/about" replace />} />
                <Route path="/services" element={<Navigate to="/leadership/services" replace />} />
                <Route path="/faq" element={<Navigate to="/leadership/faq" replace />} />
                <Route path="/contact" element={<Navigate to="/leadership/about" replace />} />
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/index" element={<Navigate to="/" replace />} />

                {/* Pilot Programme - HIDDEN (SaaS offering moved to separate site)
                <Route path="/apply" element={<PilotProgramPage />} />
                <Route path="/pilot" element={<PilotProgramPage />} />
                */}
                
                {/* Email Management Routes */}
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/email-preferences" element={<EmailPreferencesPage />} />
                
                {/* Admin Routes */}
                <Route path="/admin/analytics" element={<AdminDashboardPage />} />
                
                {/* 404 Page */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster position="bottom-right" />
      </div>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
